import type { Envelope } from "@icraftx/shared/v1";
import { ApiError } from "./api-error";

/** Base for /v1 calls. Empty = same-origin (Vite proxy in dev). */
export function apiBase(): string {
  const raw = import.meta.env.VITE_API_URL as string | undefined;
  if (raw && raw.length > 0) return raw.replace(/\/$/, "");
  return "/v1";
}

async function parseEnvelope<T>(res: Response): Promise<T> {
  const body = (await res.json()) as Envelope<T>;
  if (!body.success) {
    throw new ApiError({
      code: body.data.code || "error",
      msg: body.data.msg || body.data.code || "Request failed",
      status: res.status,
    });
  }
  return body.data;
}

export type ApiFetchOpts = {
  headers?: Record<string, string>;
  /** TanStack Query / caller abort — supersedes in-flight GETs. */
  signal?: AbortSignal;
};

export async function apiGet<T>(
  path: string,
  opts?: ApiFetchOpts,
): Promise<T> {
  const url = `${apiBase()}${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/json", ...opts?.headers },
    signal: opts?.signal,
  });
  return parseEnvelope<T>(res);
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  opts?: ApiFetchOpts,
): Promise<T> {
  const url = `${apiBase()}${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...opts?.headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return parseEnvelope<T>(res);
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const url = `${apiBase()}${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, {
    method: "PATCH",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return parseEnvelope<T>(res);
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const url = `${apiBase()}${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return parseEnvelope<T>(res);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const url = `${apiBase()}${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseEnvelope<T>(res);
}

/** Multipart upload — do not set Content-Type (boundary is automatic). */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const url = `${apiBase()}${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
    body: form,
  });
  return parseEnvelope<T>(res);
}
